import React, { useEffect, useMemo, useRef, useState } from "react";
import styled, { useTheme } from "styled-components";
import {
  FiActivity, FiAlertCircle, FiAlertTriangle, FiCheckCircle, FiChevronDown, FiChevronRight, FiDownload, FiFileText, FiGrid, FiInfo, FiSearch, FiTrash2, FiTrendingUp, FiUsers, FiX, FiXCircle,
} from "react-icons/fi";
import {
  Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import {
  Empty, Group, Pill, SearchField, Section, Widget, WidgetGrid, tokens,
} from "../activity-logs/styles";
import { SegmentedControl, Sparkline } from "../activity-logs/components/primitives";
import { IOS, colorFor } from "../activity-logs/labels";
import {
  Bento, BentoTile, CompositionWidget, CreditsPage, Notice, PeriodStats, StorageBar, Tag, TopHeadline, TopRow,
} from "../creditos/styles";
import { BackButton, ScrolledTopBar, SupplierAvatar } from "../creditos/components/parts";
import { RoundButton, TintedDanger, Toolbar } from "../creditos/components/ControlBar";
import { downloadXml } from "../creditos/xmlDocs";
import { tokens as T, hairline } from "../../styles/designTokens";
import { MISS } from "../revisor/home/AnaliseDashboard";
import { displayName } from "../revisor/home/analiseData";
import { ConfirmDelete } from "../revisor/home/RevisorHome";
import { branchLabel, formatCount, formatPercent } from "../revisor/home/revisorData";
import { PARTIAL } from "./XmlSupplierDirectory";
import XmlSupplierDashboard from "./XmlSupplierDashboard";
import { FIELD_GROUPS, batchDate, buildBatchDetail, supplierBandKey } from "./xmlScanData";

/**
 * Tela de uma análise do XML Scan. A tela inicial mostra o retrato da análise; esta
 * responde o que trava a adequação e por onde começar.
 * Elemento próprio: o caminho da adequação, com a conformidade que se alcança ao
 * corrigir os 5, 10, 20 e 50 fornecedores com mais pendências. A estrutura segue
 * Créditos: KPIs com sparkline, gráfico principal com composição, mapa com mosaico
 * e dois rankings. A API não dá série mensal, por isso o gráfico é por grupo.
 * Regras dos dados em `xmlScanData.js`.
 */

const STEP = 40;
const OPTIONAL_FIELDS = new Set(FIELD_GROUPS.filter((g) => g.optional).flatMap((g) => g.fields.map((x) => x.field)));
const CST_COLORS = [T.brand, "#6b9cf0", "#a99af0", "#f2c27a", "#8e8e93"];
const DOC_STATUS = {
  conforme: { label: "Conforme", color: T.brand },
  parcial: { label: "Parcial", color: PARTIAL },
  nao_conforme: { label: "Não conforme", color: MISS },
};

// ─── Caminho da adequação ────────────────────────────────────────────────────

const Path = styled.div`
  display: flex;
  align-items: stretch;
  gap: 10px;
  flex: 1;
  min-height: 160px;
  margin-top: 14px;

  .col { flex: 1; min-width: 0; display: flex; flex-direction: column; justify-content: flex-end; align-items: center; gap: 6px; font-variant-numeric: tabular-nums; }
  .v { font-size: 1em; font-weight: 600; letter-spacing: -0.025em; }
  .v small { font-size: 0.68em; font-weight: 500; opacity: 0.5; }
  .bar { position: relative; width: 100%; max-width: 74px; border-radius: 8px 8px 3px 3px; overflow: hidden; background: rgba(118,118,128,0.1); transition: height 800ms ${T.easeOut}; }
  .gain { position: absolute; left: 0; right: 0; top: 0; background: ${T.brand}; opacity: 0.32; }
  .base { position: absolute; left: 0; right: 0; bottom: 0; background: ${T.brand}; opacity: 0.8; }
  .l { font-size: 0.74em; color: ${({ theme }) => tokens.label2(theme)}; white-space: nowrap; }
`;

function AdequacyPath({ batch, steps }) {
  const columns = [{ key: "now", label: "Hoje", pct: batch.pct }, ...steps.map((s) => ({ key: `n${s.count}`, label: `${s.count} fornecedores`, shortLabel: `+ ${s.count}`, pct: s.pct, xmls: s.xmls }))];
  const MAX_H = 108;
  return (
    <Path role="img" aria-label={columns.map((c) => `${c.label}: ${formatPercent(c.pct)}`).join(", ")}>
      {columns.map((c) => {
        const h = (c.pct / 100) * MAX_H;
        const base = (batch.pct / 100) * MAX_H;
        return (
          <div className="col" key={c.key} title={c.xmls ? `Corrigindo ${c.label}: ${formatCount(c.xmls)} XMLs a mais` : `${formatCount(batch.conformes)} XMLs conformes hoje`}>
            <span className="v">{formatPercent(c.pct).replace("%", "")}<small>%</small></span>
            <span className="bar" style={{ height: h }}>
              {h > base && <span className="gain" style={{ height: h - base }} />}
              <span className="base" style={{ height: base }} />
            </span>
            <span className="l">{c.shortLabel || c.label}</span>
          </div>
        );
      })}
    </Path>
  );
}

// ─── Gráfico por grupo ───────────────────────────────────────────────────────

const shortName = (name) => displayName(name).split(/\s+/).filter((w) => !/^(LTDA|S\/?A|S\.A\.?|ME|EPP|-|DE|DO|DA|E)$/i.test(w)).slice(0, 2).join(" ");

function GroupTip({ active, payload }) {
  const theme = useTheme();
  const g = active && payload?.[0]?.payload;
  if (!g) return null;
  const rows = [["Conformes", g.conformes, T.brand], ["Parciais", g.parciais, PARTIAL], ["Não conformes", g.naoConformes, MISS]];
  return (
    <div style={{
      background: tokens.solid(theme), border: `1px solid ${tokens.separator(theme)}`, borderRadius: 10, padding: "10px 12px", minWidth: 190,
      fontSize: 12, fontVariantNumeric: "tabular-nums", color: tokens.label(theme), boxShadow: tokens.dk(theme) ? "0 8px 24px rgba(0,0,0,0.45)" : "0 8px 24px rgba(15,23,42,0.08)",
    }}
    >
      <div style={{ fontWeight: 600 }}>{displayName(g.name)}</div>
      <div style={{ marginBottom: 6, color: tokens.label2(theme) }}>{formatCount(g.cnpjs)} {g.cnpjs === 1 ? "CNPJ" : "CNPJs"} · {formatCount(g.total)} XMLs</div>
      {rows.map(([label, v, c]) => (
        <div key={label} style={{ display: "flex", justifyContent: "space-between", gap: 14, marginTop: 2 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: c }} />{label}</span>
          <b style={{ fontWeight: 600 }}>{formatCount(v)}</b>
        </div>
      ))}
    </div>
  );
}

// Nome do grupo e, abaixo, o total de XMLs, que é a altura da coluna
function GroupTick({ x, y, payload, data }) {
  const theme = useTheme();
  const g = data[payload.index];
  if (!g) return null;
  const narrow = window.innerWidth <= 900;
  const base = narrow ? g.label.split(" ")[0] : g.label;
  const max = narrow ? 7 : 13;
  const name = base.length > max ? `${base.slice(0, max - 1)}…` : base;
  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor="middle" dy={10} fontSize={10} fill={tokens.label(theme)}>{name}</text>
      <text textAnchor="middle" dy={23} fontSize={9.5} fill={tokens.label2(theme)}>{formatCount(g.total)}</text>
    </g>
  );
}

function GroupChart({ groups }) {
  const theme = useTheme();
  // Em tela estreita, 12 nomes não cabem no eixo: ficam os 6 maiores
  const compact = window.innerWidth <= 900;
  const data = groups.slice(0, compact ? 6 : groups.length).map((g) => ({ ...g, label: shortName(g.name) }));
  const axis = { fontSize: 10, fill: tokens.label2(theme) };
  return (
    <div style={{ height: 262 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 0, left: 0, bottom: 0 }} barCategoryGap="34%">
          <CartesianGrid vertical={false} stroke={tokens.separator(theme)} />
          <XAxis dataKey="label" tick={<GroupTick data={data} />} height={34} axisLine={false} tickLine={false} interval={0} />
          <YAxis tick={axis} axisLine={false} tickLine={false} width={46} tickFormatter={(v) => formatCount(v)} />
          <Tooltip content={<GroupTip />} cursor={{ fill: "rgba(118,118,128,0.08)", radius: 6 }} />
          <Bar dataKey="conformes" stackId="s" fill={T.brand} fillOpacity={0.8} maxBarSize={32} isAnimationActive={false} />
          <Bar dataKey="parciais" stackId="s" fill={PARTIAL} fillOpacity={0.85} maxBarSize={32} isAnimationActive={false} />
          <Bar dataKey="naoConformes" stackId="s" fill={MISS} fillOpacity={0.9} maxBarSize={32} radius={[4, 4, 0, 0]} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Mosaico das faixas ──────────────────────────────────────────────────────

const BAND_ICONS = { full: <FiCheckCircle />, high: <FiTrendingUp />, mid: <FiActivity />, low: <FiAlertCircle />, none: <FiXCircle /> };

function BandTile({ row, size, onSelect }) {
  return (
    <BentoTile type="button" $color={row.color} $size={size} disabled={row.count === 0} title={`${row.label}: ${formatCount(row.count)} fornecedores, ${formatCount(row.xmls)} XMLs`} onClick={() => onSelect(row)}>
      <span className="top">
        <span className="pct">{formatPercent(row.share).replace("%", "")}<small>%</small></span>
        <span className="icon">{BAND_ICONS[row.key]}</span>
      </span>
      <span className="bottom">
        <span className="label">{row.label}</span>
        <span className="amount">{size === "small" ? formatCount(row.count) : `${formatCount(row.count)} fornecedores`}</span>
        <span className="track"><span className="level" style={{ width: `${row.share}%`, background: row.color }} /></span>
      </span>
    </BentoTile>
  );
}

// ─── Ranking ─────────────────────────────────────────────────────────────────

function RankCard({ title, sub, share, shareLabel, rows, valueOf, total, subOf, empty, delay, onOpen, foot }) {
  return (
    <Widget $delay={delay} style={{ display: "flex", flexDirection: "column" }}>
      <div className="w-title">{title}</div>
      <div className="w-sub">{sub}</div>
      {rows.length === 0 ? <div className="w-sub" style={{ marginTop: 16 }}>{empty}</div> : (
        <>
          <TopHeadline>
            <span className="n">{formatPercent(share).replace("%", "")}<span className="pct">%</span></span>
            <span className="l">{shareLabel}</span>
          </TopHeadline>
          <StorageBar role="img" aria-label={`${formatPercent(share)} ${shareLabel}`}>
            {rows.map((s) => <span key={s.key} title={`${displayName(s.name)}: ${formatCount(valueOf(s))}`} style={{ flexGrow: valueOf(s), background: colorFor(s.name) }} />)}
            <span className="rest" style={{ flexGrow: Math.max(0, total - rows.reduce((a, s) => a + valueOf(s), 0)) }} />
          </StorageBar>
          <div style={{ marginTop: 8 }}>
            {rows.map((s) => (
              <TopRow key={s.key} type="button" onClick={() => onOpen(s.raw)}>
                <span className="lead"><SupplierAvatar name={s.name} size={32} /></span>
                <span className="main">
                  <span className="title" title={s.name}>{displayName(s.name)}</span>
                  <span className="sub" title={s.cnpj}>{subOf(s)}</span>
                </span>
                <span className="end">
                  <span className="value">{formatCount(valueOf(s))}</span>
                  <span className="share">{formatPercent((valueOf(s) / Math.max(1, total)) * 100)}</span>
                </span>
                <FiChevronRight className="chevron" />
              </TopRow>
            ))}
          </div>
          {foot && <div className="w-sub" style={{ marginTop: "auto", paddingTop: 12, fontWeight: 300, lineHeight: 1.5 }}>{foot}</div>}
        </>
      )}
    </Widget>
  );
}

// ─── Documentos ──────────────────────────────────────────────────────────────

const DocList = styled(Group)`
  margin-top: 14px;
  padding: 4px 6px 6px;
  font-variant-numeric: tabular-nums;

  .doc { position: relative; border-radius: 12px; transition: background-color ${T.durBase} ${T.easeOut}; }
  .doc + .doc::before { content: ""; position: absolute; top: 0; left: 10px; right: 10px; height: 1px; background: ${({ theme }) => hairline(theme)}; opacity: 0.7; }
  .doc:hover, .doc.open { background: rgba(118,118,128,0.07); }
  .doc:hover::before, .doc:hover + .doc::before, .doc.open::before, .doc.open + .doc::before { opacity: 0; }
  .row { display: grid; grid-template-columns: minmax(0, 1fr) 84px 112px 116px 30px; align-items: center; column-gap: 14px; padding: 8px 10px; }
  .main { min-width: 0; display: flex; align-items: center; gap: 8px; width: 100%; padding: 0; border: none; background: none; color: inherit; font: inherit; text-align: left; cursor: pointer; }
  .main:focus-visible { outline: 2px solid ${T.brand}; outline-offset: 2px; border-radius: 6px; }
  .main > svg { flex-shrink: 0; font-size: 14px; color: ${({ theme }) => tokens.label3(theme)}; transition: transform ${T.durBase} ${T.easeOut}; }
  .doc.open .main > svg { transform: rotate(180deg); }
  .txt { min-width: 0; }
  .name { font-size: 0.9em; font-weight: 500; letter-spacing: -0.01em; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .sub { margin-top: 1px; font-size: 0.76em; color: ${({ theme }) => tokens.label2(theme)}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .date, .miss { text-align: right; font-size: 0.78em; color: ${({ theme }) => tokens.label2(theme)}; white-space: nowrap; }
  .st { display: flex; justify-content: flex-end; }
  .dl { opacity: 0.55; transition: opacity ${T.durBase} ${T.easeOut}; }
  .doc:hover .dl, .dl:focus-visible { opacity: 1; }
  .more { padding: 2px 10px 12px 32px; font-size: 0.8em; }
  .more .h { margin: 8px 0 5px; font-size: 0.92em; color: ${({ theme }) => tokens.label2(theme)}; }
  .chips { display: flex; flex-wrap: wrap; gap: 4px; }
  .chip { padding: 2px 8px; border-radius: 999px; font-size: 0.9em; background: rgba(118,118,128,0.12); }
  .issue { margin-top: 3px; font-weight: 300; }
  .key { margin-top: 8px; font-size: 0.92em; font-weight: 300; color: ${({ theme }) => tokens.label2(theme)}; word-break: break-all; }
  @media (max-width: 720px) {
    .row { grid-template-columns: minmax(0, 1fr) auto 30px; }
    .date, .miss { display: none; }
  }
`;

const DocRow = React.memo(function DocRow({ d, open, onToggle }) {
  const st = DOC_STATUS[d.status] || { label: d.status || "Sem status", color: "#8e8e93" };
  const absent = d.missing.filter((m) => !OPTIONAL_FIELDS.has(m));
  const issueCounts = (d.issues || []).reduce((m, i) => { const k = i.msg ? i.msg.replace(/^Item \d+:\s*/, "") : i.code || "Alerta"; m[k] = (m[k] || 0) + 1; return m; }, {});
  return (
    <div className={`doc${open ? " open" : ""}`}>
      <div className="row">
        <button type="button" className="main" onClick={() => onToggle(d.chave)} aria-expanded={open}>
          <FiChevronDown />
          <span className="txt">
            <div className="name" title={d.supplierName}>{displayName(d.supplierName)}</div>
            <div className="sub">Nº {d.numero || "sem número"} · {branchLabel(d.supplierCnpj)}</div>
          </span>
        </button>
        <span className="date">{d.date ? d.date.toLocaleDateString("pt-BR") : "—"}</span>
        <span className="miss">{absent.length === 0 ? "Todos os campos" : `Faltam ${absent.length} campos`}</span>
        <span className="st"><Tag $color={st.color}>{st.label}</Tag></span>
        <RoundButton className="dl" type="button" onClick={() => downloadXml(d.chave)} aria-label={`Baixar XML da nota ${d.numero}`} title="Baixar XML" style={{ width: 30, height: 30 }}><FiDownload /></RoundButton>
      </div>
      {open && (
        <div className="more">
          {absent.length > 0 && (
            <>
              <div className="h">Campos obrigatórios ausentes</div>
              <div className="chips">{absent.map((m) => <span className="chip" key={m}>{m}</span>)}</div>
            </>
          )}
          {Object.keys(issueCounts).length > 0 && (
            <>
              <div className="h">Alertas</div>
              {Object.entries(issueCounts).map(([msg, n]) => <div className="issue" key={msg}>{msg}{n > 1 ? ` · ${formatCount(n)} itens` : ""}</div>)}
            </>
          )}
          <div className="key">Chave {d.chave}</div>
        </div>
      )}
    </div>
  );
});

function DocumentsTab({ docs, totalRead }) {
  const theme = useTheme();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("nao_conforme");
  const [open, setOpen] = useState(null);
  const [visible, setVisible] = useState(STEP);
  const endRef = useRef(null);

  const counts = useMemo(() => docs.reduce((m, d) => { m[d.status] = (m[d.status] || 0) + 1; return m; }, {}), [docs]);
  const list = useMemo(() => {
    const term = q.trim().toLowerCase();
    const digits = term.replace(/\D/g, "");
    return docs.filter((d) => (status === "all" || d.status === status)
      && (!term || d.supplierName.toLowerCase().includes(term) || (digits && (d.numero.includes(digits) || d.chave.includes(digits)))));
  }, [docs, q, status]);

  const hasMore = visible < list.length;
  useEffect(() => {
    if (!hasMore || !endRef.current) return undefined;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible((v) => v + STEP); }, { rootMargin: "800px" });
    io.observe(endRef.current);
    return () => io.disconnect();
  }, [hasMore, list.length, visible]);

  return (
    <>
      <Notice style={{ marginTop: 0, marginBottom: 12 }}>
        <FiInfo />
        <span>A API envia uma amostra de até 3 documentos por fornecedor: {formatCount(docs.length)} dos {formatCount(totalRead)} XMLs lidos.</span>
      </Notice>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <SearchField $max="320px">
          <FiSearch />
          <input placeholder="Fornecedor, número ou chave" aria-label="Buscar documento" value={q} onChange={(e) => { setQ(e.target.value); setVisible(STEP); }} />
          {q && <button className="clear" aria-label="Limpar busca" onClick={() => setQ("")}><FiX /></button>}
        </SearchField>
        <div style={{ flex: 1 }} />
        <SegmentedControl
          small
          value={status}
          onChange={(v) => { setStatus(v); setVisible(STEP); setOpen(null); }}
          options={[
            { key: "nao_conforme", label: "Não conformes", count: formatCount(counts.nao_conforme || 0) },
            { key: "parcial", label: "Parciais", count: formatCount(counts.parcial || 0) },
            { key: "conforme", label: "Conformes", count: formatCount(counts.conforme || 0) },
            { key: "all", label: "Todos", count: formatCount(docs.length) },
          ]}
        />
      </div>
      {list.length === 0 ? (
        <Group style={{ marginTop: 14 }}>
          <Empty>
            <div className="e-icon"><FiSearch /></div>
            <div className="e-title">Nenhum documento</div>
            <div className="e-sub">Nada encontrado com estes filtros.</div>
          </Empty>
        </Group>
      ) : (
        <DocList>
          {list.slice(0, visible).map((d) => <DocRow key={d.chave} d={d} open={open === d.chave} onToggle={(k) => setOpen((cur) => (cur === k ? null : k))} />)}
        </DocList>
      )}
      {hasMore && (
        <div ref={endRef} style={{ padding: "18px 0", textAlign: "center", fontSize: "0.78em", color: tokens.label2(theme) }}>
          {formatCount(visible)} de {formatCount(list.length)}
        </div>
      )}
    </>
  );
}

// ─── Tela ────────────────────────────────────────────────────────────────────

export default function XmlBatchDashboard({ batch: rawBatch, batches = [], onBack, onOpenSupplier, onDelete }) {
  const theme = useTheme();
  const d = useMemo(() => buildBatchDetail(rawBatch, batches), [rawBatch, batches]);
  const { batch: b, focus: f } = d;
  const [tab, setTab] = useState("overview");
  const [filter, setFilter] = useState(null);
  const [extra, setExtra] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const lastStep = d.steps[d.steps.length - 1];
  const bandRows = f.bands
    .filter((x) => x.count > 0)
    .map((x) => ({ ...x, share: f.supplierCount > 0 ? (x.count / f.supplierCount) * 100 : 0 }))
    .sort((x, y) => y.count - x.count);
  const openBand = (x) => { setFilter(null); setExtra({ label: x.label, test: (s) => supplierBandKey(s.pct) === x.key }); setTab("suppliers"); };
  const normal = f.finalities.find((x) => x.code === "1");
  const devol = f.finalities.find((x) => x.code === "4");
  const notesProfile = [
    normal && `${formatPercent(normal.share)} das notas têm finalidade normal`,
    devol && `${formatCount(devol.value)} são devoluções`,
    f.exempt > 0 && `${formatCount(f.exempt)} são anteriores a 2026, quando o grupo IBS/CBS passou a ser obrigatório`,
  ].filter(Boolean).join(", ");
  const donut = f.cst.slice(0, 5).map((c, i) => ({ ...c, color: CST_COLORS[i] }));
  const cstTotal = f.cst.reduce((s, c) => s + c.value, 0);
  const pendingWith = (key) => { setFilter(key); setExtra(null); setTab("suppliers"); };
  const dateLabel = batchDate(b);
  const openSupplier = (raw) => onOpenSupplier(raw);

  return (
    <>
      <CreditsPage>
        <ScrolledTopBar scrollToTop deps={[tab]}>
          <div className="title-row">
            <div style={{ minWidth: 0 }}>
              <BackButton onClick={onBack}>XML Scan</BackButton>
              <h1>Análise de {batchDate(b, false)}</h1>
              <div className="meta">
                {b.repeated && <Pill $color={IOS.orange}>Repetida</Pill>}
                <span className="update-tag" title={b.fileName}>{b.fileName}</span>
                <span className="update-tag">{b.id}</span>
              </div>
            </div>
          </div>
          <div className="nav-row">
            {b.failed ? <span /> : (
              <SegmentedControl
                value={tab}
                onChange={setTab}
                options={[
                  { key: "overview", icon: <FiGrid />, label: "Visão geral" },
                  { key: "suppliers", icon: <FiUsers />, label: "Fornecedores", count: formatCount(f.supplierCount) },
                  { key: "documents", icon: <FiFileText />, label: "Documentos", count: formatCount(d.documents.length) },
                ]}
              />
            )}
            <Toolbar>
              <TintedDanger type="button" onClick={() => setConfirming(true)}><FiTrash2 /> Excluir</TintedDanger>
            </Toolbar>
          </div>
        </ScrolledTopBar>

        {b.failed && (
          <Section $first>
            <Group>
              <Empty>
                <div className="e-icon"><FiAlertTriangle /></div>
                <div className="e-title">Nenhum XML foi lido nesta análise</div>
                <div className="e-sub">
                  {formatCount(b.unread)} de {formatCount(b.totalXmls)} {b.totalXmls === 1 ? "XML ficou" : "XMLs ficaram"} com erro, e a API não informa o motivo. O XML Scan confere NF-e e NFC-e; confira se o arquivo traz esses documentos.
                </div>
              </Empty>
            </Group>
          </Section>
        )}

        {!b.failed && tab === "overview" && (
          <>
            {/* KPIs */}
            <Section $first>
              <WidgetGrid>
                <Widget $delay={0} $clickable onClick={() => pendingWith("conformes")}>
                  <div className="w-head"><FiCheckCircle style={{ color: T.brand, opacity: 1 }} /> Conformidade</div>
                  <div className="w-value">{formatPercent(b.pct).replace("%", "")}<small style={{ fontSize: "0.55em", fontWeight: 500, opacity: 0.5, marginLeft: 2 }}>%</small></div>
                  <div className="w-foot" style={{ justifyContent: "space-between" }}>
                    <span>{formatCount(b.conformes)} de {formatCount(b.read)} lidos</span>
                    <Sparkline data={d.sparks.conformes} color={T.brand} width={80} height={24} points={60} />
                  </div>
                </Widget>
                <Widget $delay={50} $clickable onClick={() => pendingWith("parciais")}>
                  <div className="w-head"><span style={{ width: 7, height: 7, borderRadius: "50%", background: PARTIAL, opacity: 1 }} /> Parciais</div>
                  <div className="w-value">{formatCount(b.parciais)}</div>
                  <div className="w-foot" style={{ justifyContent: "space-between" }}>
                    <span>em {formatCount(f.rankings.parciais.count)} fornecedores</span>
                    <Sparkline data={d.sparks.parciais} color={PARTIAL} width={80} height={24} points={60} />
                  </div>
                </Widget>
                <Widget $delay={100} $clickable onClick={() => pendingWith("naoConformes")}>
                  <div className="w-head"><span style={{ width: 7, height: 7, borderRadius: "50%", background: MISS, opacity: 1 }} /> Não conformes</div>
                  <div className="w-value">{formatCount(b.naoConformes)}</div>
                  <div className="w-foot" style={{ justifyContent: "space-between" }}>
                    <span>em {formatCount(f.rankings.naoConformes.count)} fornecedores</span>
                    <Sparkline data={d.sparks.naoConformes} color={MISS} width={80} height={24} points={60} />
                  </div>
                </Widget>
                <Widget $delay={150}>
                  <div className="w-head"><FiAlertTriangle style={{ color: IOS.red, opacity: 1 }} /> Alertas críticos</div>
                  <div className="w-value">{formatCount(d.criticalTotal)}</div>
                  <div className="w-foot" style={{ justifyContent: "space-between" }}>
                    <span>itens de {formatCount(d.criticalCount)} fornecedores</span>
                    <Sparkline data={d.sparks.critical} color={IOS.red} width={80} height={24} points={60} />
                  </div>
                </Widget>
              </WidgetGrid>
            </Section>

            {/* Gráfico principal + composição */}
            <Section $delay={80}>
              <WidgetGrid>
                <Widget $span={3} $delay={200}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 6, flexWrap: "wrap" }}>
                    <div style={{ minWidth: 0 }}>
                      <div className="w-title">Situação por grupo empresarial</div>
                      <div className="w-sub">
                        Altura proporcional ao total de XMLs dos maiores grupos, entre os {formatCount(d.groupCount)} desta análise. {formatCount(b.unread)} dos {formatCount(b.totalXmls)} recebidos não foram lidos e ficam fora da conta.
                      </div>
                    </div>
                    <div className="w-eyebrow" style={{ display: "flex", gap: 14 }}>
                      {[["Conformes", T.brand], ["Parciais", PARTIAL], ["Não conformes", MISS]].map(([label, c]) => (
                        <span key={label} style={{ display: "flex", alignItems: "center", gap: 5 }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: c }} />{label}</span>
                      ))}
                    </div>
                  </div>
                  <GroupChart groups={d.groups} />
                </Widget>

                <CompositionWidget $delay={250}>
                  <div className="w-title">Situação tributária</div>
                  <div className="w-sub">CST declarado nos XMLs</div>
                  {donut.length > 0 ? (
                    <>
                      <div style={{ position: "relative", height: 150, marginTop: 12 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie data={donut} dataKey="value" nameKey="name" innerRadius={56} outerRadius={68} cornerRadius={4} paddingAngle={donut.length > 1 ? 3 : 0} stroke="none" startAngle={90} endAngle={-270} isAnimationActive={false}>
                              {donut.map((c) => <Cell key={c.code} fill={c.color} />)}
                            </Pie>
                          </PieChart>
                        </ResponsiveContainer>
                        <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                          <b style={{ fontSize: 20, fontWeight: 700, letterSpacing: "-0.03em" }}>{formatCount(cstTotal)}</b>
                          <span style={{ fontSize: 11, color: tokens.label2(theme) }}>declarações</span>
                        </div>
                      </div>
                      <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 7 }}>
                        {donut.slice(0, 3).map((c) => (
                          <div key={c.code} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, fontSize: "0.82em" }} title={`CST ${c.code}: ${formatCount(c.value)}`}>
                            <span style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
                              <span style={{ flexShrink: 0, width: 7, height: 7, borderRadius: "50%", background: c.color }} />
                              <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.name}</span>
                            </span>
                            <span style={{ flexShrink: 0, color: tokens.label2(theme), fontVariantNumeric: "tabular-nums" }}>{formatPercent(c.share)}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : <div className="w-sub" style={{ marginTop: 12 }}>Nenhum CST declarado.</div>}
                </CompositionWidget>
              </WidgetGrid>
            </Section>

            {/* Por onde começar + mosaico */}
            <Section $delay={140}>
              <WidgetGrid>
                <Widget $span={2} $delay={300} style={{ display: "flex", flexDirection: "column" }}>
                  <div className="w-title">Por onde começar</div>
                  <div className="w-sub">
                    Conformidade simulada se todos os XMLs dos fornecedores com mais pendências passarem a trazer os campos
                    {f.parcial410 >= b.parciais / 2 && b.parciais > 0 ? ". Parte dos parciais pode ser CST 410, que normalmente não leva valores" : ""}
                  </div>
                  {d.steps.length > 0 ? <AdequacyPath batch={b} steps={d.steps} /> : <div className="w-sub" style={{ marginTop: 16 }}>Nenhum fornecedor com pendência.</div>}
                  <PeriodStats style={{ marginTop: 14 }}>
                    <div><span className="v">{formatCount(d.pendingXmls)}</span><span className="l">XMLs pendentes</span></div>
                    <div><span className="v">{formatCount(d.pendingCount)}</span><span className="l">Fornecedores</span></div>
                    <div><span className="v">{lastStep ? formatCount(lastStep.left) : "—"}</span><span className="l">{lastStep ? `Restam com ${formatCount(lastStep.count)}` : "Restantes"}</span></div>
                  </PeriodStats>
                </Widget>

                <Widget $span={2} $delay={350} style={{ display: "flex", flexDirection: "column" }}>
                  <div className="w-title">Fornecedores por adequação</div>
                  <div className="w-sub">Parte dos XMLs de cada fornecedor que está conforme. Clique para ver a lista</div>
                  <Bento>
                    {bandRows[0] && <BandTile row={bandRows[0]} size="large" onSelect={openBand} />}
                    {bandRows[1] && (
                      <div className="side">
                        <BandTile row={bandRows[1]} size="medium" onSelect={openBand} />
                        {bandRows.length > 2 && <div className="base">{bandRows.slice(2).map((r) => <BandTile key={r.key} row={r} size="small" onSelect={openBand} />)}</div>}
                      </div>
                    )}
                  </Bento>
                </Widget>
              </WidgetGrid>
            </Section>

            {/* Rankings */}
            <Section $delay={200}>
              <WidgetGrid $cols={2}>
                <RankCard
                  title="Alertas críticos"
                  sub="Itens de nota sem o grupo IBS/CBS"
                  share={d.criticalShare}
                  shareLabel={`dos alertas estão em ${formatCount(d.criticalSuppliers.length)} fornecedores`}
                  rows={d.criticalSuppliers}
                  valueOf={(s) => s.critical}
                  total={d.criticalTotal}
                  subOf={(s) => `${branchLabel(s.cnpj)} · ${formatCount(s.naoConformes)} XMLs não conformes`}
                  empty="Nenhum alerta crítico nesta análise."
                  delay={400}
                  onOpen={openSupplier}
                />
                <RankCard
                  title="Maiores emissores"
                  sub="Quem tem mais XMLs nesta análise"
                  share={d.volumeShare}
                  shareLabel={`dos XMLs lidos vêm de ${formatCount(d.volumeTop.length)} fornecedores`}
                  rows={d.volumeTop}
                  valueOf={(s) => s.total}
                  total={b.read}
                  subOf={(s) => `${branchLabel(s.cnpj)} · ${formatPercent(s.pct)} conformes`}
                  empty="Nenhum fornecedor nesta análise."
                  delay={450}
                  onOpen={openSupplier}
                  foot={notesProfile}
                />
              </WidgetGrid>
            </Section>
          </>
        )}

        {!b.failed && tab === "suppliers" && (
          <XmlSupplierDashboard
            suppliers={f.suppliers}
            filter={filter}
            onFilter={setFilter}
            extra={extra}
            onExtra={setExtra}
            onOpen={openSupplier}
          />
        )}

        {!b.failed && tab === "documents" && (
          <Section $first>
            <DocumentsTab docs={d.documents} totalRead={b.read} />
          </Section>
        )}
      </CreditsPage>

      {confirming && (
        <ConfirmDelete
          name={dateLabel}
          onCancel={() => setConfirming(false)}
          onConfirm={() => { setConfirming(false); onDelete(b.operationId); }}
        />
      )}
    </>
  );
}
